import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProviderVerificationAlertProps {
  status: 'verified' | 'pending' | 'inactive' | 'rejected';
  message?: string;
}

export const ProviderVerificationAlert = ({ 
  status, 
  message 
}: ProviderVerificationAlertProps) => {
  const navigate = useNavigate();

  const getStatusConfig = () => {
    switch (status) {
      case 'verified':
        return {
          icon: CheckCircle,
          className: 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800',
          iconClassName: 'text-green-600',
          title: 'Profile Verified',
          description: message || 'Your profile is live and visible to customers searching for services.',
          showAction: false
        };
      case 'pending':
        return {
          icon: Clock,
          className: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-800',
          iconClassName: 'text-yellow-600',
          title: 'Verification Pending',
          description: message || 'Your profile is under review. You\'ll be notified once approved.',
          showAction: false
        };
      case 'inactive':
        return {
          icon: AlertCircle,
          className: 'bg-gray-50 border-gray-200 dark:bg-gray-900/10 dark:border-gray-800',
          iconClassName: 'text-gray-600',
          title: 'Profile Inactive',
          description: message || 'Complete your registration and payment to activate your profile.',
          showAction: true,
          actionText: 'Complete Registration',
          actionPath: '/register-provider'
        };
      case 'rejected':
        return {
          icon: XCircle,
          className: 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800',
          iconClassName: 'text-red-600',
          title: 'Verification Rejected',
          description: message || 'Your profile verification was rejected. Please contact support for more information.',
          showAction: true,
          actionText: 'Contact Support',
          actionPath: '/contact'
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <Alert className={config.className}>
      <Icon className={`h-4 w-4 ${config.iconClassName}`} />
      <AlertDescription>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold mb-1">{config.title}</h3>
            <p className="text-sm">{config.description}</p>
          </div>
          {config.showAction && (
            <Button 
              size="sm" 
              onClick={() => navigate(config.actionPath || '/')}
            >
              {config.actionText}
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};
